import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ShieldCheck, 
  FileText, 
  Search, 
  Phone, 
  AlertTriangle,
  IndianRupee,
  Users,
  FileCheck,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [stats, setStats] = useState({
    total_amount_lost: 0,
    total_cases: 0,
    firs_filed: 0,
    cases_resolved: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API}/statistics`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    } else if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(2)} K`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const features = [
    {
      icon: FileText,
      title: "Report a Scam",
      description: "Submit your complaint in simple steps. We guide you through the entire process.",
      link: "/report",
      color: "#e74c3c"
    },
    {
      icon: Search,
      title: "Track Your Case",
      description: "Check the status of your complaint anytime using your Case ID.",
      link: "/track",
      color: "#2c3e50"
    },
    {
      icon: Phone,
      title: "Get Help",
      description: "Learn about your rights, helplines, and steps to take after a fraud.",
      link: "/knowledge",
      color: "#27ae60"
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-24" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#2c3e50] leading-tight font-['Manrope'] mb-6">
                Recovering Dignity & Funds for Elders
              </h1>
              <p className="text-lg md:text-xl text-[#546e7a] leading-relaxed mb-8">
                A safe space to report scams, track investigations, and find support. We help senior citizens navigate the process of reporting financial fraud.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/report" data-testid="hero-report-btn">
                  <Button className="w-full sm:w-auto bg-[#e74c3c] hover:bg-[#c0392b] text-white text-lg font-bold py-6 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
                    <AlertTriangle className="w-6 h-6 mr-2" />
                    REPORT A SCAM NOW
                  </Button>
                </Link>
                <Link to="/track" data-testid="hero-track-btn">
                  <Button variant="outline" className="w-full sm:w-auto border-2 border-[#2c3e50] text-[#2c3e50] text-lg font-semibold py-6 px-8 rounded-lg hover:bg-slate-50 transition-all">
                    <Search className="w-5 h-5 mr-2" />
                    Check Case Status
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="hidden lg:block"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_protect-elders/artifacts/4643dnu5_Reset%20password%20photo.JPG"
                alt="Senior citizen empowered by Elderra"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
                data-testid="hero-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Live Statistics Section */}
      <section className="py-16 bg-white" data-testid="stats-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
              Live Dashboard
            </h2>
            <p className="text-lg text-[#546e7a]">
              Real-time statistics from our platform
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Total Amount Lost */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="stat-card border-l-4 border-l-[#e74c3c] bg-white shadow-md hover:shadow-lg" data-testid="stat-amount-lost">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-red-50 rounded-full">
                      <IndianRupee className="w-8 h-8 text-[#e74c3c]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#546e7a] font-medium uppercase tracking-wide">Total Amount Lost</p>
                      <p className="text-3xl md:text-4xl font-bold text-[#2c3e50] font-['Manrope']">
                        {loading ? "..." : formatCurrency(stats.total_amount_lost)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Total Cases */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="stat-card border-l-4 border-l-[#2c3e50] bg-white shadow-md hover:shadow-lg" data-testid="stat-total-cases">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-slate-100 rounded-full">
                      <Users className="w-8 h-8 text-[#2c3e50]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#546e7a] font-medium uppercase tracking-wide">Cases Reported</p>
                      <p className="text-3xl md:text-4xl font-bold text-[#2c3e50] font-['Manrope']">
                        {loading ? "..." : stats.total_cases.toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* FIRs Filed */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Card className="stat-card border-l-4 border-l-[#3498db] bg-white shadow-md hover:shadow-lg" data-testid="stat-firs-filed">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-50 rounded-full">
                      <FileCheck className="w-8 h-8 text-[#3498db]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#546e7a] font-medium uppercase tracking-wide">FIRs Filed</p>
                      <p className="text-3xl md:text-4xl font-bold text-[#2c3e50] font-['Manrope']">
                        {loading ? "..." : stats.firs_filed.toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Cases Resolved */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <Card className="stat-card border-l-4 border-l-[#27ae60] bg-white shadow-md hover:shadow-lg" data-testid="stat-cases-resolved">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-50 rounded-full">
                      <ShieldCheck className="w-8 h-8 text-[#27ae60]" />
                    </div>
                    <div>
                      <p className="text-sm text-[#546e7a] font-medium uppercase tracking-wide">Resolved</p>
                      <p className="text-3xl md:text-4xl font-bold text-[#2c3e50] font-['Manrope']">
                        {loading ? "..." : stats.cases_resolved.toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-[#F8F9FA]" data-testid="features-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#2c3e50] font-['Manrope'] mb-4">
              How We Help You
            </h2>
            <p className="text-lg text-[#546e7a] max-w-2xl mx-auto">
              Simple, step-by-step assistance for senior citizens affected by financial fraud
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link to={feature.link} data-testid={`feature-card-${index}`}>
                  <Card className="h-full bg-white border-t-4 shadow-md hover:shadow-xl transition-all cursor-pointer group" style={{ borderTopColor: feature.color }}>
                    <CardContent className="p-8">
                      <div 
                        className="p-4 rounded-full w-fit mb-6"
                        style={{ backgroundColor: `${feature.color}15` }}
                      >
                        <feature.icon 
                          className="w-10 h-10" 
                          style={{ color: feature.color }} 
                        />
                      </div>
                      <h3 className="text-2xl font-bold text-[#2c3e50] font-['Manrope'] mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-base text-[#546e7a] leading-relaxed mb-4">
                        {feature.description}
                      </p>
                      <div className="flex items-center gap-2 font-semibold group-hover:gap-3 transition-all" style={{ color: feature.color }}>
                        <span>Learn More</span>
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Helpline Banner */}
      <section className="py-12 bg-[#2c3e50]" data-testid="helpline-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-2xl md:text-3xl font-bold text-white font-['Manrope'] mb-2">
                Need Immediate Help?
              </h3>
              <p className="text-lg text-slate-300">
                Call the National Cyber Crime Helpline
              </p>
            </div>
            <a 
              href="tel:1930" 
              className="flex items-center gap-3 bg-[#e74c3c] text-white text-2xl font-bold py-4 px-8 rounded-lg hover:bg-[#c0392b] transition-colors shadow-lg"
              data-testid="helpline-call-btn"
            >
              <Phone className="w-8 h-8" />
              <span>1930</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
